import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Modal, Typography, Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import styles from './TripDetails.module.scss';
import { useGetTripQuery, useDeleteTripMutation } from '../../../store/services/tripsApi';
import { TripStop } from '../../../types/trip.types';
import CustomButton from '../../../common/CustomButton';
import TripHeader from './components/TripHeader';
import CreateActivityModal from './components/CreateActivityModal';
import CreateTripModal from '../MyTrips/components/CreateTripModal';
import TripDetailsTabs from './components/TripDetailsTabs/TripDetailsTabs';
import TravelBuddies from './components/TravelBuddies/TravelBuddies';
import TripTimeline from './components/TripTimeline/TripTimeline';
import StopDetails from './components/StopDetails/StopDetails';
import FlightDelayPrediction from './components/FlightDelayPrediction/FlightDelayPrediction';
import TripRightPanelCalendar from './components/TripRightPanelCalendar/TripRightPanelCalendar';
import TripRightPanelMap from './components/TripRightPanelMap/TripRightPanelMap';
import TripRightPanelFilters, {
  TripRightPanelFiltersInterface as RightPanelFiltersType,
} from './components/TripRightPanelFilters/TripRightPanelFilters';
import TravelContactsPanel from './components/TravelContactsPanel/TravelContactsPanel';
import TripPackingPanel from './components/TripPackingPanel/TripPackingPanel';
import TripFlightAlternativesPanel from './components/TripFlightAlternativesPanel/TripFlightAlternativesPanel';
import { MOBILE_FULL_SCREEN_MODAL_Z, MODAL_ABOVE_FULL_SCREEN_Z } from '../../../constants/modalZIndex';
import AIAssistant from './components/AIAssistant/AIAssistant';
import GoNowPanel from './components/GoNowPanel/GoNowPanel';
import { useIsMobile } from '../../../hooks/useWindowWidth';
import { GlobalIcon, DateIcon, AiIcon } from '../../../components/Icons';
import AboutCountry from './components/AboutCountry/AboutCountry';
import CountryDetailsPanel from './components/AboutCountry/CountryDetailsPanel/CountryDetailsPanel';
import { useAppSelector } from '../../../hooks/redux';
import TripDocuments from './components/TripDocuments/TripDocuments';
import TripPackingList from './components/TripPackingList/TripPackingList';
import FullAccessModal from '../../../common/FullAccessModal/FullAccessModal';
import { PremiumFeature } from '../../../common/premiumFeatures';

enum RightPanelContentType {
  HIDDEN = 'hidden',
  CALENDAR = 'calendar',
  MAP = 'map',
  FILTERS = 'filters',
  STOP_DETAILS = 'stop_details',
  FLIGHT_DELAY_PREDICTION = 'flight_delay_prediction',
  AI_ASSISTANT = 'ai_assistant',
  TRAVEL_CONTACTS = 'travel_contacts',
  GO_NOW = 'go_now',
  PACKING_LIST = 'packing_list',
  FLIGHT_ALTERNATIVES = 'flight_alternatives',
  COUNTRY_DETAILS = 'country_details',
}

interface RightPanelState {
  isVisible: boolean;
  contentType: RightPanelContentType;
  data?: any;
}

type MobileDisplayMode = 'timeline' | 'map' | 'calendar' | 'ai';

const TripDetails: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const user = useAppSelector(state => state.user);

  const [selectedTab, setSelectedTab] = useState('schedule');
  const [showCreateActivityModal, setShowCreateActivityModal] = useState(false);
  const [showEditTripModal, setShowEditTripModal] = useState(false);
  const [fromIdeaSuccessSnackbarOpen, setFromIdeaSuccessSnackbarOpen] = useState(false);
  const [fromIdeaSuccessMessage, setFromIdeaSuccessMessage] = useState('');

  const { data: trip, isLoading, error, refetch } = useGetTripQuery(id!);
  const [deleteTrip] = useDeleteTripMutation();

  const [rightPanelState, setRightPanelState] = useState<RightPanelState>({
    isVisible: true,
    contentType: RightPanelContentType.CALENDAR,
    data: null,
  });

  const [rightPanelFilters, setRightPanelFilters] = useState<RightPanelFiltersType>({
    displayFormat: 'calendar',
  });

  const [mobileDisplayMode, setMobileDisplayMode] = useState<MobileDisplayMode>('timeline');
  const [mapExpanded, setMapExpanded] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [openFullAccessModal, setOpenFullAccessModal] = useState(false);
  const [fullAccessFeature, setFullAccessFeature] = useState<PremiumFeature | undefined>();
  const scheduleHeaderRef = useRef<HTMLDivElement | null>(null);
  const [showFloatingAddStopButton, setShowFloatingAddStopButton] = useState(false);

  useEffect(() => {
    if (!user.is_premium && mobileDisplayMode === 'ai') {
      setMobileDisplayMode('timeline');
    }
    if (!user.is_premium && mobileDisplayMode === 'map') {
      setMobileDisplayMode('timeline');
      setMapExpanded(false);
    }
  }, [user.is_premium, mobileDisplayMode]);

  useEffect(() => {
    const state = location.state as {
      createdFromIdea?: boolean;
      ideaToTripPreToast?: boolean;
      createdTripName?: string;
      sourceIdeaName?: string;
    } | null;
    if (state?.createdFromIdea && state.ideaToTripPreToast) {
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: {} }
      );
      return;
    }
    if (state?.createdFromIdea && !state.ideaToTripPreToast) {
      const tripName = state.createdTripName?.trim();
      const ideaName = state.sourceIdeaName?.trim();
      const line =
        tripName && ideaName
          ? `Trip '${tripName}' was successfully created from your idea '${ideaName}'. Your idea list is unchanged.`
          : tripName
            ? `Trip '${tripName}' was successfully created from your idea. Your idea list is unchanged.`
            : 'Trip was successfully created from your idea. Your idea list is unchanged.';
      setFromIdeaSuccessMessage(line);
      setFromIdeaSuccessSnackbarOpen(true);
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: {} }
      );
    }
  }, [id, location.pathname, location.search, location.hash, location.state, navigate]);

  const updateFloatingAddStopVisibility = useCallback(() => {
    if (selectedTab !== 'schedule') {
      setShowFloatingAddStopButton(false);
      return;
    }

    const target = scheduleHeaderRef.current;
    if (!target) {
      setShowFloatingAddStopButton(false);
      return;
    }

    const { top } = target.getBoundingClientRect();
    setShowFloatingAddStopButton(top < 0);
  }, [selectedTab]);

  useEffect(() => {
    updateFloatingAddStopVisibility();

    const handleViewportChange = () => updateFloatingAddStopVisibility();
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);

    return () => {
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [updateFloatingAddStopVisibility]);

  useEffect(() => {
    const shouldFlipPageScrollbar = rightPanelState.isVisible && !isMobile;
    if (shouldFlipPageScrollbar) {
      document.documentElement.classList.add('page-scrollbar-left');
    } else {
      document.documentElement.classList.remove('page-scrollbar-left');
    }
    return () => {
      document.documentElement.classList.remove('page-scrollbar-left');
    };
  }, [rightPanelState.isVisible, isMobile]);

  const [showStopDetailsModal, setShowStopDetailsModal] = useState(false);
  const [selectedStopForModal, setSelectedStopForModal] = useState<TripStop | null>(null);

  const getPreferredSchedulePanelContentType = useCallback((): RightPanelContentType => {
    return rightPanelFilters.displayFormat === 'map' && user.is_premium
      ? RightPanelContentType.MAP
      : RightPanelContentType.CALENDAR;
  }, [rightPanelFilters.displayFormat, user.is_premium]);

  const handleShowRightPanel = useCallback((contentType: RightPanelContentType, data?: any) => {
    setRightPanelState({
      isVisible: true,
      contentType,
      data,
    });

    if (contentType === RightPanelContentType.CALENDAR) {
      setRightPanelFilters(prev => ({ ...prev, displayFormat: 'calendar' }));
    } else if (contentType === RightPanelContentType.MAP) {
      setRightPanelFilters(prev => ({ ...prev, displayFormat: 'map' }));
    }
  }, []);

  const handleHideRightPanel = useCallback(() => {
    setRightPanelState(prev => {
      if (
        prev.contentType === RightPanelContentType.CALENDAR ||
        prev.contentType === RightPanelContentType.MAP
      ) {
        return { ...prev, isVisible: false };
      }

      return {
        isVisible: false,
        contentType: RightPanelContentType.HIDDEN,
        data: null,
      };
    });
  }, []);

  const handleToggleRightPanel = useCallback(() => {
    if (rightPanelState.isVisible) {
      handleHideRightPanel();
    } else {
      const contentType =
        rightPanelState.contentType === RightPanelContentType.MAP ||
        rightPanelState.contentType === RightPanelContentType.CALENDAR
          ? rightPanelState.contentType
          : getPreferredSchedulePanelContentType();

      handleShowRightPanel(contentType);
    }
  }, [
    rightPanelState.isVisible,
    rightPanelState.contentType,
    handleHideRightPanel,
    handleShowRightPanel,
    getPreferredSchedulePanelContentType,
  ]);

  const handleExpandIconClick = useCallback(() => {
    handleToggleRightPanel();
  }, [handleToggleRightPanel]);

  const handleShowStopDetails = useCallback(
    (activity: TripStop) => {
      if (isMobile) {
        setSelectedStopForModal(activity);
        setShowStopDetailsModal(true);
        setRightPanelState(prev => ({
          ...prev,
          isVisible: false,
          contentType: RightPanelContentType.STOP_DETAILS,
          data: activity,
        }));
      } else {
        handleShowRightPanel(RightPanelContentType.STOP_DETAILS, activity);
      }
    },
    [isMobile, handleShowRightPanel]
  );

  const handleShowFlightDelayPrediction = useCallback(
    (activityId: number) => {
      const currentActivity = rightPanelState.data;
      handleShowRightPanel(RightPanelContentType.FLIGHT_DELAY_PREDICTION, {
        activityId,
        activity: currentActivity,
      });
    },
    [rightPanelState.data, handleShowRightPanel]
  );

  const handleBackToStopDetails = useCallback(
    (activity: TripStop) => {
      handleShowRightPanel(RightPanelContentType.STOP_DETAILS, activity);
    },
    [handleShowRightPanel]
  );

  const handleShowFilters = useCallback(() => {
    handleShowRightPanel(RightPanelContentType.FILTERS);
  }, [handleShowRightPanel]);

  const handleCloseFilters = useCallback(() => {
    if (isMobile) {
      setMobileFiltersOpen(false);
      return;
    }

    setRightPanelState({
      isVisible: true,
      contentType: getPreferredSchedulePanelContentType(),
      data: null,
    });
  }, [isMobile, getPreferredSchedulePanelContentType]);

  const handleFiltersChange = useCallback(
    (newFilters: RightPanelFiltersType) => {
      if (!user.is_premium && newFilters.displayFormat === 'map') {
        setFullAccessFeature('map');
        setOpenFullAccessModal(true);
        return;
      }

      setRightPanelFilters(newFilters);

      if (isMobile) {
        setMobileFiltersOpen(false);
        if (newFilters.displayFormat === 'map') {
          setMobileDisplayMode('map');
          setMapExpanded(false);
        } else {
          setMobileDisplayMode(prev => (prev === 'map' ? 'timeline' : prev));
        }
        return;
      }

      if (newFilters.displayFormat === 'calendar') {
        setRightPanelState({
          isVisible: true,
          contentType: RightPanelContentType.CALENDAR,
          data: null,
        });
      } else if (newFilters.displayFormat === 'map') {
        setRightPanelState({
          isVisible: true,
          contentType: RightPanelContentType.MAP,
          data: null,
        });
      }
    },
    [user.is_premium, isMobile]
  );

  const handleShowTravelContacts = useCallback(
    (activity: TripStop) => {
      handleShowRightPanel(RightPanelContentType.TRAVEL_CONTACTS, activity);
    },
    [handleShowRightPanel]
  );

  const handleShowPackingList = useCallback(
    (_activity: TripStop) => {
      if (!user.is_premium) {
        setFullAccessFeature('packing_list');
        setOpenFullAccessModal(true);
        return;
      }
      handleShowRightPanel(RightPanelContentType.PACKING_LIST, _activity);
    },
    [handleShowRightPanel, user.is_premium]
  );

  const handleShowAlternativeFlights = useCallback(
    (activity: TripStop) => {
      handleShowRightPanel(RightPanelContentType.FLIGHT_ALTERNATIVES, activity);
    },
    [handleShowRightPanel]
  );

  const handleShowGoNow = useCallback(
    (activity: TripStop) => {
      handleShowRightPanel(RightPanelContentType.GO_NOW, activity);
    },
    [handleShowRightPanel]
  );

  const handleGoNowSuccess = useCallback(() => {
    console.log('Go Now reminder set successfully');
    handleHideRightPanel();
  }, [handleHideRightPanel]);

  const handleShowCountryDetails = useCallback(
    (countryData: any) => {
      handleShowRightPanel(RightPanelContentType.COUNTRY_DETAILS, countryData);
    },
    [handleShowRightPanel]
  );

  const handleAIButtonClick = useCallback(() => {
    if (!user.is_premium) {
      setFullAccessFeature('ai_assistant');
      setOpenFullAccessModal(true);
      return;
    }
    if (mobileDisplayMode === 'ai') {
      setMobileDisplayMode('timeline');
    } else {
      setMobileDisplayMode('ai');
    }
  }, [mobileDisplayMode, user.is_premium]);

  const handleFullAccessModalClose = useCallback(() => {
    setOpenFullAccessModal(false);
    if (fullAccessFeature === 'map') {
      setRightPanelFilters(prev => ({ ...prev, displayFormat: 'calendar' }));
      setMobileFiltersOpen(false);
    }
    setFullAccessFeature(undefined);
  }, [fullAccessFeature]);

  const handleMapButtonClick = useCallback(() => {
    if (!user.is_premium) {
      setFullAccessFeature('map');
      setOpenFullAccessModal(true);
      return;
    }
    if (mobileDisplayMode === 'map') {
      setMapExpanded(!mapExpanded);
    } else {
      setMobileDisplayMode('map');
      setMapExpanded(false);
    }
  }, [user.is_premium, mobileDisplayMode, mapExpanded]);

  const handleCalendarButtonClick = useCallback(() => {
    setMobileDisplayMode('calendar');
  }, []);

  const handleMapExpand = useCallback(() => {
    setMapExpanded(!mapExpanded);
  }, [mapExpanded]);

  const handleBackClick = useCallback(() => {
    navigate('/trips');
  }, [navigate]);

  const handleAIAssistant = useCallback(() => {
    if (!user.is_premium) {
      setFullAccessFeature('ai_assistant');
      setOpenFullAccessModal(true);
      return;
    }
    if (isMobile) {
      setMobileDisplayMode('ai');
    } else {
      handleShowRightPanel(RightPanelContentType.AI_ASSISTANT);
    }
  }, [isMobile, handleShowRightPanel, user.is_premium]);

  const handleEditTrip = useCallback(() => {
    setShowEditTripModal(true);
  }, []);

  const handleDeleteTrip = useCallback(async () => {
    if (!trip) return;

    const tripName = trip.name?.trim() || 'Trip';
    try {
      await deleteTrip(trip.id.toString()).unwrap();
      navigate('/trips', {
        state: { tripDeletedToast: true, deletedTripName: tripName },
      });
    } catch (error) {
      console.error('Failed to delete trip:', error);
    }
  }, [trip, deleteTrip, navigate]);

  const handleEditTripModalClose = useCallback(() => {
    setShowEditTripModal(false);
  }, []);

  const handleEditTripSuccess = useCallback(() => {
    console.log('Trip updated successfully');
    refetch();
    setShowEditTripModal(false);
  }, [refetch]);

  const handleTabChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newTab: string | null) => {
      if (newTab === 'packing-list' && !user.is_premium) {
        setFullAccessFeature('packing_list');
        setOpenFullAccessModal(true);
        return;
      }
      if (newTab !== null) setSelectedTab(newTab);
    },
    [user.is_premium]
  );

  const handleAddNewStop = useCallback(() => {
    setShowCreateActivityModal(true);
  }, []);

  const handleCloseActivityModal = useCallback(() => {
    setShowCreateActivityModal(false);
  }, []);

  const handleActivityCreated = useCallback(() => {
    console.log('Activity created successfully');
    refetch();
  }, [refetch]);

  const handleMergeSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleRefetchTrip = useCallback(() => {
    refetch();
  }, [refetch]);

  const shouldShowCompactMap = () => {
    return isMobile && mobileDisplayMode === 'map' && !mapExpanded;
  };

  const shouldShowExpandedMap = () => {
    return isMobile && mobileDisplayMode === 'map' && mapExpanded;
  };

  const shouldShowCalendar = () => {
    return isMobile && mobileDisplayMode === 'calendar';
  };

  const shouldShowAI = () => {
    return isMobile && mobileDisplayMode === 'ai' && user.is_premium;
  };

  const shouldShowTimeline = () => {
    if (!isMobile) return true;

    return mobileDisplayMode === 'timeline' || (mobileDisplayMode === 'map' && !mapExpanded);
  };

  const shouldShowTabs = () => {
    if (!isMobile) return true;

    return mobileDisplayMode !== 'calendar' && mobileDisplayMode !== 'ai';
  };

  const renderRightPanelContent = () => {
    if (!trip || isMobile) return null;

    switch (rightPanelState.contentType) {
      case RightPanelContentType.CALENDAR:
        return (
          <TripRightPanelCalendar
            trip={trip}
            onShowStopDetails={handleShowStopDetails}
            onShowFilters={handleShowFilters}
            onTogglePanel={handleToggleRightPanel}
          />
        );
      case RightPanelContentType.MAP:
        return (
          <TripRightPanelMap
            trip={trip}
            onShowStopDetails={handleShowStopDetails}
            onShowFilters={handleShowFilters}
            onTogglePanel={handleToggleRightPanel}
          />
        );
      case RightPanelContentType.STOP_DETAILS:
        return (
          <StopDetails
            activity={rightPanelState.data}
            onClose={handleHideRightPanel}
            onShowFlightDelayPrediction={handleShowFlightDelayPrediction}
            onRefetchTrip={handleRefetchTrip}
            onShowTravelContacts={handleShowTravelContacts}
            onShowGoNow={handleShowGoNow}
            onShowPackingList={handleShowPackingList}
            onShowAlternativeFlights={handleShowAlternativeFlights}
            tripId={id}
          />
        );
      case RightPanelContentType.FLIGHT_DELAY_PREDICTION:
        return (
          <FlightDelayPrediction
            activityId={rightPanelState.data.activityId}
            onBack={() => handleBackToStopDetails(rightPanelState.data.activity)}
            onClose={handleHideRightPanel}
          />
        );
      case RightPanelContentType.AI_ASSISTANT:
        return (
          <AIAssistant
            tripId={id!}
            onClose={handleHideRightPanel}
          />
        );
      case RightPanelContentType.TRAVEL_CONTACTS:
        return (
          <TravelContactsPanel
            activity={rightPanelState.data}
            tripId={parseInt(id!)}
            onClose={handleHideRightPanel}
            onRefetchTrip={handleRefetchTrip}
          />
        );
      case RightPanelContentType.PACKING_LIST:
        return (
          <TripPackingPanel
            tripId={parseInt(id!)}
            stopId={rightPanelState.data?.id}
            onClose={handleHideRightPanel}
          />
        );
      case RightPanelContentType.FLIGHT_ALTERNATIVES:
        return rightPanelState.data?.id != null ? (
          <TripFlightAlternativesPanel
            tripId={parseInt(id!)}
            stopId={rightPanelState.data.id}
            numberOfPassengers={(rightPanelState.data as TripStop & { number_of_passengers?: number })
              .number_of_passengers}
            onClose={handleHideRightPanel}
          />
        ) : null;
      case RightPanelContentType.GO_NOW:
        return (
          <GoNowPanel
            activity={rightPanelState.data}
            onClose={handleHideRightPanel}
            onSuccess={handleGoNowSuccess}
          />
        );
      case RightPanelContentType.COUNTRY_DETAILS:
        return (
          <CountryDetailsPanel
            countryData={rightPanelState.data}
            onClose={handleHideRightPanel}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box className={styles.tripDetails}>
        <Box className={styles.loadingContainer}>
          <Typography variant="h6">Loading trip details...</Typography>
        </Box>
      </Box>
    );
  }

  if (error || !trip) {
    return (
      <Box className={styles.tripDetails}>
        <Box className={styles.errorContainer}>
          <Typography
            variant="h6"
            color="error"
            sx={{ mb: 2 }}
          >
            Trip not found
          </Typography>
          <CustomButton
            variant="outlined"
            onClick={handleBackClick}
          >
            Back to Trips
          </CustomButton>
        </Box>
      </Box>
    );
  }

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'schedule':
        return (
          <Box className={styles.scheduleContainer}>
            <Box
              ref={scheduleHeaderRef}
              className={styles.scheduleHeader}
            >
              <Typography
                variant="h7"
                className={styles.sectionTitle}
              >
                Trip Schedule
              </Typography>
            </Box>
            <Box
              className={`${styles.addStopButtonSticky} ${showFloatingAddStopButton ? styles.inlineButtonHidden : ''}`}
            >
              <CustomButton
                variant="contained"
                onClick={handleAddNewStop}
                customClass={styles.addStopButton}
                startIcon={
                  <AddIcon
                    sx={{
                      fontSize: 24,
                      width: 24,
                      height: 24,
                    }}
                  />
                }
              >
                {!isMobile && 'Add New Stop'}
              </CustomButton>
            </Box>

            <Box className={styles.scheduleTimeline}>
              <TripTimeline
                trip={trip}
                onShowStopDetails={handleShowStopDetails}
                selectedActivityId={
                  rightPanelState.contentType === RightPanelContentType.STOP_DETAILS ||
                  rightPanelState.contentType === RightPanelContentType.PACKING_LIST ||
                  rightPanelState.contentType === RightPanelContentType.FLIGHT_ALTERNATIVES
                    ? rightPanelState.data?.id
                    : null
                }
                onCloseDetails={handleHideRightPanel}
              />
            </Box>
          </Box>
        );

      case 'about-country':
        return (
          <AboutCountry
            countries={trip.destination_countries}
            onShowCountryDetails={handleShowCountryDetails}
          />
        );

      case 'travel-buddies':
        return <TravelBuddies tripId={parseInt(id!)} />;

      case 'documents':
        return <TripDocuments tripId={parseInt(id!)} />;

      case 'packing-list':
        return user.is_premium ? <TripPackingList tripId={parseInt(id!)} /> : null;

      default:
        return null;
    }
  };

  return (
    <>
      <Box className={styles.tripDetails}>
        <Box className={styles.tripContent}>
          <Box
            className={`${styles.contentWrapper} ${
              rightPanelState.isVisible && !isMobile ? styles.rightPanelVisible : styles.rightPanelHidden
            }`}
          >
            <Box className={styles.leftColumn}>
              <TripHeader
                //@ts-expect-error
                trip={trip}
                onBackClick={handleBackClick}
                onAIAssistant={handleAIAssistant}
                onMergeSuccess={handleMergeSuccess}
                onEditTrip={handleEditTrip}
                onDeleteTrip={handleDeleteTrip}
                rightPanelVisible={rightPanelState.isVisible}
                onExpandClick={handleExpandIconClick}
              />

              {shouldShowCompactMap() && (
                <Box className={styles.compactMapSection}>
                  <TripRightPanelMap
                    trip={trip}
                    onShowStopDetails={handleShowStopDetails}
                    onShowFilters={() => {}}
                    onTogglePanel={handleMapExpand}
                    isExpanded={false}
                    showExpandButton={true}
                  />
                </Box>
              )}

              {shouldShowTabs() && (
                <TripDetailsTabs
                  selectedTab={selectedTab}
                  onTabChange={handleTabChange}
                />
              )}

              <Box className={styles.mainContentArea}>
                {shouldShowTimeline() && renderTabContent()}

                {shouldShowExpandedMap() && (
                  <Box className={styles.expandedMapContainer}>
                    <TripRightPanelMap
                      trip={trip}
                      onShowStopDetails={handleShowStopDetails}
                      onShowFilters={() => {}}
                      onTogglePanel={handleMapExpand}
                      isExpanded={true}
                      showExpandButton={true}
                    />
                  </Box>
                )}

                {shouldShowCalendar() && (
                  <Box className={styles.calendarModeContainer}>
                    <TripRightPanelCalendar
                      trip={trip}
                      onShowStopDetails={handleShowStopDetails}
                      onShowFilters={() => {}}
                      onTogglePanel={() => setMobileDisplayMode('timeline')}
                      showCloseButton={false}
                    />
                    {selectedTab === 'schedule' && (
                      <Box className={styles.calendarAllStopsSection}>
                        <Typography
                          variant="h7"
                          className={styles.sectionTitle}
                        >
                          Trip Schedule
                        </Typography>
                        <Box className={styles.scheduleTimeline}>
                          <TripTimeline
                            trip={trip}
                            onShowStopDetails={handleShowStopDetails}
                            selectedActivityId={
                              rightPanelState.contentType === RightPanelContentType.STOP_DETAILS ||
                              rightPanelState.contentType === RightPanelContentType.PACKING_LIST ||
                              rightPanelState.contentType === RightPanelContentType.FLIGHT_ALTERNATIVES
                                ? rightPanelState.data?.id
                                : null
                            }
                            onCloseDetails={handleHideRightPanel}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {shouldShowAI() && (
                  <>
                    <Box className={styles.aiModeHeader}>
                      <Typography
                        variant="h5Bold"
                        className={styles.aiTitle}
                      >
                        AI Assistant
                      </Typography>
                      <Box className={styles.aiDisclaimer}>
                        <Typography
                          variant="h11"
                          className={styles.aiDisclaimerText}
                        >
                          AI responses may not always be accurate—always verify important details.
                        </Typography>
                      </Box>
                    </Box>

                    <Box className={styles.aiModeContainer}>
                      <AIAssistant
                        tripId={id!}
                        onClose={() => setMobileDisplayMode('timeline')}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Box>

            {!isMobile &&
              rightPanelState.isVisible &&
              rightPanelState.contentType !== RightPanelContentType.FILTERS && (
                <Box className={styles.rightColumn}>{renderRightPanelContent()}</Box>
              )}
          </Box>
        </Box>

        {((!isMobile && rightPanelState.contentType === RightPanelContentType.FILTERS) ||
          (isMobile && mobileFiltersOpen)) && (
          <TripRightPanelFilters
            filters={rightPanelFilters}
            onFiltersChange={handleFiltersChange}
            onClose={handleCloseFilters}
          />
        )}

        {selectedTab === 'schedule' && showFloatingAddStopButton && (
          <Box
            className={`${styles.floatingAddStopButton} ${
              rightPanelState.isVisible && !isMobile ? styles.withRightPanel : ''
            }`}
          >
            <CustomButton
              variant="contained"
              onClick={handleAddNewStop}
              customClass={styles.addStopButton}
              startIcon={
                <AddIcon
                  sx={{
                    fontSize: 24,
                    width: 24,
                    height: 24,
                  }}
                />
              }
            >
              {!isMobile && 'Add New Stop'}
            </CustomButton>
          </Box>
        )}

        {isMobile && (
          <Box className={styles.mobileActionButtons}>
            <Box
              className={styles.aiButton}
              onClick={handleAIButtonClick}
            >
              <AiIcon size={24} />
            </Box>

            <Box
              className={styles.mapButton}
              onClick={handleMapButtonClick}
            >
              <GlobalIcon
                size={24}
                color="#ffffff"
              />
            </Box>

            <Box
              className={styles.calendarButton}
              onClick={handleCalendarButtonClick}
            >
              <DateIcon
                size={24}
                color="#ffffff"
              />
            </Box>
          </Box>
        )}

        <CreateActivityModal
          open={showCreateActivityModal}
          onClose={handleCloseActivityModal}
          onSuccess={handleActivityCreated}
          tripId={parseInt(id!)}
        />

        <CreateTripModal
          open={showEditTripModal}
          onClose={handleEditTripModalClose}
          onSuccess={handleEditTripSuccess}
          isEditMode={true}
          //@ts-expect-error
          tripData={trip}
        />

        {isMobile && (
          <Modal
            open={showStopDetailsModal && Boolean(selectedStopForModal)}
            onClose={() => {
              setShowStopDetailsModal(false);
              setSelectedStopForModal(null);
              if (isMobile) {
                setRightPanelState({
                  isVisible: true,
                  contentType: RightPanelContentType.CALENDAR,
                  data: null,
                });
              }
            }}
            sx={{ zIndex: MOBILE_FULL_SCREEN_MODAL_Z }}
            slotProps={{
              backdrop: { sx: { backgroundColor: 'rgba(34, 43, 58, 0.45)' } },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                maxHeight: '100dvh',
                bgcolor: '#fff',
                outline: 'none',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                pt: `calc(12px + env(safe-area-inset-top, 0px))`,
                pr: `calc(16px + env(safe-area-inset-right, 0px))`,
                pb: `calc(20px + env(safe-area-inset-bottom, 0px))`,
                pl: `calc(16px + env(safe-area-inset-left, 0px))`,
              }}
            >
              {selectedStopForModal ? (
                rightPanelState.contentType === RightPanelContentType.PACKING_LIST &&
                rightPanelState.data?.id === selectedStopForModal.id ? (
                  <TripPackingPanel
                    tripId={parseInt(id!)}
                    stopId={selectedStopForModal.id}
                    onClose={() => {
                      handleShowRightPanel(RightPanelContentType.STOP_DETAILS, selectedStopForModal);
                    }}
                  />
                ) : rightPanelState.contentType === RightPanelContentType.FLIGHT_ALTERNATIVES &&
                  rightPanelState.data?.id === selectedStopForModal.id ? (
                  <TripFlightAlternativesPanel
                    tripId={parseInt(id!)}
                    stopId={selectedStopForModal.id}
                    numberOfPassengers={(selectedStopForModal as TripStop & { number_of_passengers?: number })
                      .number_of_passengers}
                    onClose={() => {
                      handleShowRightPanel(RightPanelContentType.STOP_DETAILS, selectedStopForModal);
                    }}
                  />
                ) : rightPanelState.contentType === RightPanelContentType.TRAVEL_CONTACTS &&
                  rightPanelState.data?.id === selectedStopForModal.id ? (
                  <TravelContactsPanel
                    activity={rightPanelState.data}
                    tripId={parseInt(id!)}
                    onClose={() => {
                      handleShowRightPanel(RightPanelContentType.STOP_DETAILS, selectedStopForModal);
                    }}
                    onRefetchTrip={handleRefetchTrip}
                  />
                ) : rightPanelState.contentType === RightPanelContentType.FLIGHT_DELAY_PREDICTION &&
                  rightPanelState.data?.activity?.id === selectedStopForModal.id ? (
                  <FlightDelayPrediction
                    activityId={rightPanelState.data.activityId}
                    onBack={() => {
                      handleShowRightPanel(RightPanelContentType.STOP_DETAILS, selectedStopForModal);
                    }}
                    onClose={() => {
                      setShowStopDetailsModal(false);
                      setSelectedStopForModal(null);
                      setRightPanelState({
                        isVisible: true,
                        contentType: RightPanelContentType.CALENDAR,
                        data: null,
                      });
                    }}
                  />
                ) : rightPanelState.contentType === RightPanelContentType.GO_NOW &&
                  rightPanelState.data?.id === selectedStopForModal.id ? (
                  <GoNowPanel
                    activity={rightPanelState.data}
                    onClose={() => {
                      handleShowRightPanel(RightPanelContentType.STOP_DETAILS, selectedStopForModal);
                    }}
                    onSuccess={() => {
                      handleShowRightPanel(RightPanelContentType.STOP_DETAILS, selectedStopForModal);
                    }}
                  />
                ) : (
                  <StopDetails
                    activity={selectedStopForModal}
                    onClose={() => {
                      setShowStopDetailsModal(false);
                      setSelectedStopForModal(null);
                      if (isMobile) {
                        setRightPanelState({
                          isVisible: true,
                          contentType: RightPanelContentType.CALENDAR,
                          data: null,
                        });
                      }
                    }}
                    onShowFlightDelayPrediction={handleShowFlightDelayPrediction}
                    onRefetchTrip={handleRefetchTrip}
                    onShowTravelContacts={handleShowTravelContacts}
                    onShowGoNow={handleShowGoNow}
                    onShowPackingList={handleShowPackingList}
                    onShowAlternativeFlights={handleShowAlternativeFlights}
                    tripId={id}
                    nestedModalZIndex={MODAL_ABOVE_FULL_SCREEN_Z}
                  />
                )
              ) : null}
            </Box>
          </Modal>
        )}
      </Box>

      <FullAccessModal
        open={openFullAccessModal}
        feature={fullAccessFeature}
        onClose={handleFullAccessModalClose}
      />

      <Snackbar
        open={fromIdeaSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setFromIdeaSuccessSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setFromIdeaSuccessSnackbarOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {fromIdeaSuccessMessage ||
            'Trip was successfully created from your idea. Your idea list is unchanged.'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TripDetails;
